import Send from '@mui/icons-material/Send';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useQuery } from '@tanstack/react-query';
import { matchSorter } from 'match-sorter';
import { LoadingButton } from 'components/loadingbutton/LoadingButton';
import { signeesQueries } from 'hooks/signeesQueries';
import { DesignTokenProvider, designToken } from '@proposify/ui';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { trackMixpanelEvent } from '@proposify/frontend-observability';
import { useCurrentUser } from 'providers/UserProvider';
import type { ShareDocumentApiPayload } from 'services/documents/DocumentRepository';
import type { SigneeEntity } from 'services/entities/SigneeEntity';
import { EmailTemplateRichTextEditor } from 'pages/settings/email-templates/components/EmailTemplateRichTextEditor/EmailTemplateRichTextEditor';
import { EmailSearchInput } from './EmailSearchInput';
import { EmailTextInput } from './EmailTextInput';
import { EmailFormLayout } from './EmailFormLayout';
import { transformEmailDataToPayload } from './utils/transformEmailDataToPayload';
import { EmailTemplateEventType } from 'pages/settings/email-templates/types';
import { emailFormTestIds } from './EmailForm.test-ids';

export interface EmailFormDefaultValues {
  to?: SigneeEntity[];
  cc?: SigneeEntity[];
  bcc?: SigneeEntity[];
  from: string;
  subject?: string;
  body?: string;
}

interface EmailFormProps {
  accountId: number;
  isSending: boolean;
  signeeOptions: SigneeEntity[];
  handleSubmit: (data: ShareDocumentApiPayload, fromEmails: string[], toEmails: string[]) => void;
  defaultValues?: EmailFormDefaultValues;
}

export const EmailForm: React.FC<EmailFormProps> = ({ accountId, defaultValues, signeeOptions, handleSubmit, isSending }) => {
  const { t } = useTranslation();
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState<{ id?: string; eventType: EmailTemplateEventType } | null>(null);

  const { data: currentUser } = useCurrentUser();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchText(searchText), 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const trimmedSearchText = debouncedSearchText.trim();

  const { data: searchData } = useQuery({
    ...signeesQueries.search(accountId, trimmedSearchText),
    enabled: trimmedSearchText.length > 0,
  });

  const formMethods = useForm({
    defaultValues: {
      to: [],
      cc: [],
      bcc: [],
      from: '',
      subject: '',
      body: '',
      ...defaultValues,
    },
  });

  useEffect(() => {
    if (!showCc) {
      formMethods.setValue('cc', []);
    }
    if (!showBcc) {
      formMethods.setValue('bcc', []);
    }
  }, [showCc, showBcc]);

  const handleTemplateSelect = (option: { subject: string; templateId?: string; eventType: EmailTemplateEventType }) => {
    formMethods.setValue('subject', option.subject);
    setSelectedEmailTemplate({ id: option.templateId, eventType: option.eventType });
  };

  const handleSendEmail = () => {
    const payload = transformEmailDataToPayload(formMethods.getValues());

    if (selectedEmailTemplate) {
      trackMixpanelEvent({
        eventName: 'Email Template Selected',
        properties: {
          distinct_id: currentUser?.id?.toString(),
          account_id: accountId.toString(),
          email_template_id: selectedEmailTemplate.id,
          email_template_type: selectedEmailTemplate.eventType,
          page_url: window.location.href,
        },
      });
    }

    handleSubmit(payload, [formMethods.getValues().from], formMethods.getValues().to?.map(({ email }) => email) || []);
  };

  const allEmailOptions = useMemo(() => {
    const searchResults = searchData?.signees ?? [];
    const merged = [...signeeOptions, ...searchResults.filter((s) => !signeeOptions.some((o) => o.id === s.id))];
    return merged.map((signee) => ({ ...signee, value: signee.email }));
  }, [signeeOptions, searchData]);

  const backendMatchedIds = useMemo(() => new Set((searchData?.signees ?? []).map((signee) => signee.id)), [searchData]);

  const keepBackendMatches = <Option extends { id: number | string; email: string }>(
    options: Option[],
    { inputValue }: { inputValue: string }
  ) => {
    const localMatches = matchSorter(options, inputValue, { keys: ['email'] });
    const localMatchIds = new Set(localMatches.map((option) => option.id));
    return [...localMatches, ...options.filter((option) => backendMatchedIds.has(option.id) && !localMatchIds.has(option.id))];
  };

  const emailFields = [
    {
      name: 'to' as const,
      label: 'To',
      show: true,
      limitTags: 3,
      showButtons: !showCc && !showBcc,
    },
    {
      name: 'cc' as const,
      label: 'Cc',
      show: showCc,
      limitTags: -1,
      showButtons: !showBcc,
    },
    {
      name: 'bcc' as const,
      label: 'Bcc',
      show: showBcc,
      limitTags: -1,
      showButtons: !showCc,
    },
  ];

  const getButtonConfig = (fieldName: string) => {
    switch (fieldName) {
      case 'to':
        return [
          { label: 'Cc', onClick: () => setShowCc(!showCc) },
          { label: 'Bcc', onClick: () => setShowBcc(!showBcc) },
        ];
      case 'cc':
        return [{ label: 'Bcc', onClick: () => setShowBcc(!showBcc) }];
      case 'bcc':
        return [{ label: 'Cc', onClick: () => setShowCc(!showCc) }];
      default:
        return [];
    }
  };

  return (
    <DesignTokenProvider theme={designToken}>
      <EmailFormLayout.Root>
        {emailFields.map(
          (field) =>
            field.show && (
              <Stack key={field.name} position="relative" data-testid={emailFormTestIds.recipientField(field.name)}>
                <EmailSearchInput
                  name={field.name}
                  control={formMethods.control}
                  limitTags={field.limitTags}
                  label={field.label}
                  options={allEmailOptions}
                  onInputChange={setSearchText}
                  filterOptions={keepBackendMatches}
                />
                {field.showButtons && (
                  <Stack direction="row" spacing={0} position="absolute" top={12} right={12} bgcolor="white">
                    {getButtonConfig(field.name).map((button) => (
                      <Button
                        key={button.label}
                        variant="text"
                        size="small"
                        onClick={button.onClick}
                        sx={{ textDecoration: 'underline', m: 0 }}
                      >
                        {button.label}
                      </Button>
                    ))}
                  </Stack>
                )}
              </Stack>
            )
        )}
        <EmailTextInput name="from" label="From" control={formMethods.control} disabled />
        <EmailTextInput name="subject" label="Subject" control={formMethods.control} disabled={false} />
        <EmailFormLayout.Body>
          <Controller
            name="body"
            control={formMethods.control}
            render={({ field, fieldState: { error } }) => (
              <EmailTemplateRichTextEditor
                emailTemplatesEventTypesAllowed={[EmailTemplateEventType.DOCUMENT_SHARE]}
                errorMessage={error?.message}
                onChange={field.onChange}
                onTemplateSelect={handleTemplateSelect}
                placeholder={t('share_document_v3.email_share_modal.body_placeholder')}
                showEmailTemplateOptions
                value={field.value}
              />
            )}
          />
        </EmailFormLayout.Body>
        <EmailFormLayout.Actions>
          <LoadingButton
            data-testid={emailFormTestIds.sendButton}
            variant="contained"
            color="primary"
            startIcon={<Send />}
            onClick={handleSendEmail}
            isLoading={isSending}
            disabled={formMethods.watch('to')?.length === 0 || formMethods.watch('subject') === ''}
          >
            {t('share_document_v3.email_share_modal.send_button')}
          </LoadingButton>
        </EmailFormLayout.Actions>
      </EmailFormLayout.Root>
    </DesignTokenProvider>
  );
};
